import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import { toDate } from '@cbuild/controls';
import { ActiveFormStateControl } from './active-form-state.control';

// @dynamic
export class CsValidators {
  public static creditCardValidator(
    control: AbstractControl
  ): { invalidCreditCard: boolean } | null {
    // Visa, MasterCard, American Express, Diners Club, Discover, JCB
    if (
      control.value &&
      control.value.match(
        // tslint:disable-next-line: max-line-length
        /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/
      )
    ) {
      return null;
    } else {
      return { invalidCreditCard: true };
    }
  }

  public static emailValidator(
    control: AbstractControl
  ): { invalidEmailAddress: boolean } | null {
    // RFC 2822 compliant regex
    if (control.value) {
      if (
        control.value.match(
          // tslint:disable-next-line: max-line-length
          /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9]{2}(?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/
        )
      ) {
        return null;
      } else {
        return { invalidEmailAddress: true };
      }
    } else {
      return null;
    }
  }

  public static passwordValidator(
    control: AbstractControl
  ): { invalidPassword: boolean } | null {
    // {6,100}           - Assert password is between 6 and 100 characters
    // (?=.*[0-9])       - Assert a string has at least one number
    // (?!.*\s)          - Spaces are not allowed
    if (
      control.value &&
      control.value.match(/^(?=.*\d)(?=.*[a-zA-Z!@#$%^&*])(?!.*\s).{6,100}$/)
    ) {
      return null;
    } else {
      return { invalidPassword: true };
    }
  }

  public static duplicateNameCheckerWithFormArray(
    items: FormArray,
    colName: string
  ): ValidatorFn {
    return (control: AbstractControl): null | { nameConflicted: boolean } => {
      if (items.controls.length > 0) {
        for (const refControl of items.controls) {
          if (control !== refControl.get(colName)) {
            if (
              control.value.toLowerCase() ===
              refControl.get(colName)!.value.toLowerCase()
            ) {
              return { nameConflicted: true };
            }
          }
        }
      }
      return null;
    };
  }

  public static duplicateNameCheckerWithArray(
    // tslint:disable-next-line: no-any
    items: Array<any>, // FIXME Try remove no-any
    colName: string
  ): ValidatorFn {
    return (control: AbstractControl): null | { nameConflicted: boolean } => {
      if (control.value) {
        for (const item of items) {
          if (
            colName in item &&
            item[colName] &&
            item[colName].toLowerCase() === control.value.toLowerCase()
          ) {
            return { nameConflicted: true };
          }
        }
      }
      return null;
    };
  }

  public static duplicateFloorsCheckForFloorRise(
    sourceItems: Array<{ fromValue: number; toValue: number }>
  ): ValidatorFn {
    return (control: AbstractControl): null | { floorExists: boolean } => {
      const result = sourceItems.filter(
        x =>
          (x.fromValue <= control.value.min &&
            x.toValue >= control.value.min) ||
          (x.fromValue <= control.value.max && x.toValue >= control.value.max)
      );
      if (result.length > 0) {
        return { floorExists: true };
      }
      return null;
    };
  }

  public static totalPercentageValidator(totalPercentage: number): ValidatorFn {
    return (
      control: AbstractControl
    ): { invalidPercentage: boolean } | null => {
      if (parseFloat(control.value) + totalPercentage > 100) {
        return { invalidPercentage: true };
      }
      return null;
    };
  }

  /* decimal validation for default 2 decimal values */
  public static decimalNumberValidation(
    maxNumber: number | null = null,
    decimalPoints: number = 2
  ): ValidatorFn {
    return (
      control: AbstractControl
    ): { invalidMaxNumber?: boolean; invalidDecimal?: boolean } | null => {
      if (control.value) {
        const regex = new RegExp(
          `^[0-9]\\d{0,9}(\\.\\d{1,${decimalPoints}})?$`,
          'g'
        );
        if (control.value.match(regex)) {
          const num = parseFloat(control.value);
          if (maxNumber != null) {
            if (num > maxNumber) {
              return { invalidMaxNumber: true };
            }
          }
        } else {
          return { invalidDecimal: true };
        }
      }
      return null;
    };
  }

  public static numberValidation(
    minNumber: number | null = null,
    maxNumber: number | null = null
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        if (control.value.match(/^[0-9]\d{0,9}/)) {
          if (
            (minNumber && control.value < minNumber) ||
            (maxNumber && control.value > maxNumber)
          ) {
            return { invalidNumber: true };
          }
        } else {
          return { invalidNumber: true };
        }
      }
      return null;
    };
  }

  public static startDateRangeValidator(
    form: ActiveFormStateControl,
    endDate = 'endDate'
  ): ValidatorFn {
    return (control: AbstractControl): null | { invalidStartDate: boolean } => {
      if (form.infoForm) {
        const endDateControl = form.infoForm.get(endDate)!;
        if (endDateControl.hasError('invalidEndDate')) {
          delete endDateControl.errors?.invalidEndDate;
          endDateControl.updateValueAndValidity();
        }
        if (endDateControl.value) {
          if (new Date(control.value) > new Date(endDateControl.value)) {
            return { invalidStartDate: true };
          }
        }
      }
      return null;
    };
  }

  public static endDateRangeValidator(
    form: ActiveFormStateControl,
    startDate = 'startDate'
  ): ValidatorFn {
    return (control: AbstractControl): null | { invalidEndDate: boolean } => {
      if (form.infoForm) {
        const startDateControl = form.infoForm.get(startDate)!;
        if (startDateControl.hasError('invalidStartDate')) {
          delete startDateControl.errors?.invalidStartDate;
          startDateControl.updateValueAndValidity();
        }
        if (startDateControl.value) {
          if (new Date(startDateControl.value) > new Date(control.value)) {
            return { invalidEndDate: true };
          }
        }
      }
      return null;
    };
  }

  public static minimumRangeValidator(
    component: ActiveFormStateControl,
    name: string
  ): ValidatorFn {
    return (control: AbstractControl): null | { invalidMinimum: boolean } => {
      if (component.infoForm) {
        const maxControl = component.infoForm.get(name)!;
        if (maxControl.hasError('invalidMaximum')) {
          delete maxControl.errors?.invalidMaximum;
          maxControl.updateValueAndValidity();
        }
        if (maxControl.value) {
          if (parseInt(control.value, 0) > parseInt(maxControl.value, 0)) {
            return { invalidMinimum: true };
          }
        }
      }
      return null;
    };
  }

  public static maximumRangeValidator(
    component: ActiveFormStateControl,
    name: string
  ): ValidatorFn {
    return (control: AbstractControl): null | { invalidMaximum: boolean } => {
      if (component.infoForm) {
        const minControl = component.infoForm.get(name)!;
        if (minControl.hasError('invalidMinimum')) {
          delete minControl.errors?.invalidMinimum;
          minControl.updateValueAndValidity();
        }
        if (minControl.value) {
          if (parseInt(minControl.value, 0) > parseInt(control.value, 0)) {
            return { invalidMaximum: true };
          }
        }
      }
      return null;
    };
  }

  public static websiteValidator(
    control: AbstractControl
  ): { invalidWebsite: boolean } | null {
    if (
      control.value &&
      !control.value.match(
        /^http(s?)\:\/\/[0-9a-zA-Z]?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/
      )
    ) {
      return { invalidWebsite: true };
    }
    return null;
  }

  public static clampedEndDateValidator(
    form: ActiveFormStateControl
  ): ValidatorFn {
    return (control: AbstractControl): null | { invalidEndDate: boolean } => {
      if (form.infoForm) {
        const startDate = form.infoForm.get('startDate')!;
        if (startDate.hasError('invalidStartDate')) {
          delete startDate.errors?.invalidStartDate;
          startDate.updateValueAndValidity();
        }
        if (startDate.value != null) {
          const start = new Date(startDate.value).setHours(0, 0, 0, 0);
          const end = new Date(control.value).setHours(23, 59, 59, 999);
          if (start > end) {
            return { invalidEndDate: true };
          } else if (start <= end) {
            return null;
          }
        }
      }
      return null;
    };
  }

  public static clampedStartDateValidator(
    form: ActiveFormStateControl
  ): ValidatorFn {
    return (control: AbstractControl): null | { invalidStartDate: boolean } => {
      if (form.infoForm) {
        const endDate = form.infoForm.get('endDate')!;
        if (endDate.hasError('invalidEndDate')) {
          delete endDate.errors?.invalidEndDate;
          endDate.updateValueAndValidity();
        }
        if (endDate.value != null) {
          const start = new Date(control.value).setHours(0, 0, 0, 0);
          const end = new Date(endDate.value).setHours(23, 59, 59, 999);
          if (start > end) {
            return { invalidStartDate: true };
          } else if (start <= end) {
            return null;
          }
        }
      }
      return null;
    };
  }

  public static combinedFileSize(size: number): ValidatorFn {
    return (_: AbstractControl): null | { maxFileSizeReached: boolean } => {
      const val = _.value;
      if (val instanceof FileList) {
        let totalSize = 0;
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < val.length; ++i) {
          totalSize += val[i].size;
        }
        if (totalSize >= size) {
          return { maxFileSizeReached: true };
        }
      }
      return null;
    };
  }

  // public static percentageValidator(control: AbstractControl): any {
  //   if (
  //     control.value &&
  //     control.value.match(/^\d+([.]\d+)?%?/) &&
  //     control.value < 100
  //   ) {
  //     return undefined;
  //   } else {
  //     return { invalidPassword: true };
  //   }
  // }

  // CsValidators class
  public static emptyFormArray(): ValidatorFn {
    return (control: AbstractControl): { emptyFormArray: boolean } | null => {
      const formArray = control as FormArray;
      if (formArray.length === 0) {
        return {
          emptyFormArray: true
        };
      }
      return null;
    };
  }

  public static dateRangeValidator(
    startDate: string = 'startDate',
    endDate: string = 'endDate',
    errName: string = 'invalidDateRange'
  ): ValidatorFn {
    return (control: AbstractControl): null | { [key: string]: boolean } => {
      const formGroup = control as FormGroup;
      const startDateValue = formGroup.get(startDate)?.value;
      const endDateValue = formGroup.get(endDate)?.value;

      if (!startDateValue || !endDateValue) {
        return null;
      } else if (
        toDate(startDateValue).getTime() > toDate(endDateValue).getTime()
      ) {
        return { [errName]: true };
      }

      return null;
    };
  }
}
